import { convexQuery } from "@convex-dev/react-query";
import type { Doc, Id } from "@convex/_generated/dataModel";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import {
  type CategoryListItem,
  categoriesGetForMenu,
  itemsCreate,
  salesCategoriesGetAll,
  taxCategoriesGetAll,
} from "~/api-refs";
import { ImageDropzone } from "~/components/shared/image-dropzone";
import { Button } from "~/components/ui/button";
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { ProductEvents } from "~/lib/posthog";

import { CategoryFields } from "./category-fields";
import { PriceInput } from "./price-input";
import { type ItemFormData, itemFormSchema, type ModalTypes } from "./types";

// ── Data-fetching wrapper ────────────────────────────────────────────────────

export const CreateNewItemForm = ({
  menuId,
  onFinished,
  item,
  setModalType,
  menuName,
  handleClose,
  searchParams,
}: {
  menuId: string;
  item?: ItemFormData;
  onFinished: () => void;
  setModalType: (type: ModalTypes | null) => void;
  menuName?: string;
  handleClose: () => void;
  searchParams: { newSalesCategoryId?: string; newTaxCategoryId?: string };
}) => {
  const { data: categories } = useQuery(
    convexQuery(categoriesGetForMenu, {
      menuId: toConvexTableId("menus", menuId),
      includeMenus: true,
      includeItemsCount: true,
    })
  );
  const { data: taxCategories } = useQuery(
    convexQuery(taxCategoriesGetAll, {})
  );
  const { data: salesCategories } = useQuery(
    convexQuery(salesCategoriesGetAll, {})
  );

  return (
    <CreateNewItemActualForm
      categories={categories}
      handleClose={handleClose}
      item={item}
      menuId={menuId}
      menuName={menuName}
      onFinished={onFinished}
      salesCategories={salesCategories}
      searchParams={searchParams}
      setModalType={setModalType}
      taxCategories={taxCategories}
    />
  );
};

// ── Actual form ──────────────────────────────────────────────────────────────

const emptyTaxCategories: Doc<"taxCategories">[] = [];

const CreateNewItemActualForm = ({
  categories,
  taxCategories = emptyTaxCategories,
  onFinished,
  menuId,
  item,
  setModalType,
  salesCategories,
  menuName,
  handleClose,
  searchParams,
}: {
  categories: CategoryListItem[] | undefined;
  taxCategories: Doc<"taxCategories">[] | undefined;
  salesCategories: Doc<"salesCategories">[] | undefined;
  menuId: string;
  item?: ItemFormData;
  onFinished: () => void;
  setModalType: (type: ModalTypes | null) => void;
  menuName?: string;
  handleClose: () => void;
  searchParams: { newSalesCategoryId?: string; newTaxCategoryId?: string };
}) => {
  const navigate = useNavigate();
  const { newSalesCategoryId, newTaxCategoryId } = searchParams;
  const selectedCategories = item?.categories?.length ? item.categories : [];
  const form = useForm<ItemFormData>({
    resolver: zodResolver(itemFormSchema),
    defaultValues: item
      ? {
          ...item,
          categories: selectedCategories,
          price: item.price?.length ? item.price : "",
        }
      : {
          name: "",
          shortName: "",
          description: "",
          price: "",
          isOutOfStock: false,
          imageUrl: null,
          categories: selectedCategories,
          taxCategoryId: null,
          salesCategoryId: null,
        },
  });
  const formTaxCategoryIdWatch = form.watch("taxCategoryId");

  // Handle search params from child category creation routes
  useEffect(() => {
    if (newSalesCategoryId) {
      form.setValue("salesCategoryId", newSalesCategoryId);
      void navigate({
        to: `/menus/${menuId}/items/new`,
        search: {},
        replace: true,
      });
    }
  }, [newSalesCategoryId, form, navigate, menuId]);

  useEffect(() => {
    if (newTaxCategoryId) {
      form.setValue("taxCategoryId", newTaxCategoryId);
      void navigate({
        to: `/menus/${menuId}/items/new`,
        search: {},
        replace: true,
      });
    }
  }, [newTaxCategoryId, form, navigate, menuId]);

  useEffect(() => {
    if (taxCategories?.[0] && !formTaxCategoryIdWatch) {
      const untaxedIndex = taxCategories.findIndex(
        (category) => category.value === 0
      );
      if (taxCategories[untaxedIndex]) {
        form.setValue("taxCategoryId", taxCategories[untaxedIndex]._id);
      }
    }
  }, [taxCategories, form.setValue, formTaxCategoryIdWatch, form]);

  const createItemMutation = useMutation({
    mutationFn: (data: {
      name: string;
      shortName: string;
      description: string;
      price: number;
      isOutOfStock: boolean;
      imageUrl?: string;
      taxCategoryId?: Id<"taxCategories">;
      salesCategoryId?: Id<"salesCategories">;
      categories: Id<"categories">[];
    }) => {
      return convexClient.mutation(itemsCreate, data);
    },
    onSuccess: (result, variables) => {
      ProductEvents.menuItemCreated(result.id, variables.categories[0] ?? "");
    },
  });

  const handleFormSubmit = async (data: ItemFormData) => {
    if (createItemMutation.isPending) {
      return undefined;
    }
    try {
      const returnValue = await createItemMutation.mutateAsync({
        ...data,
        imageUrl: data.imageUrl ?? undefined,
        price:
          (typeof data.price === "string"
            ? Number.parseFloat(data.price)
            : data.price) ?? 0,
        taxCategoryId:
          data.taxCategoryId === "0" || !data.taxCategoryId
            ? undefined
            : toConvexTableId("taxCategories", data.taxCategoryId),
        salesCategoryId: data.salesCategoryId
          ? toConvexTableId("salesCategories", data.salesCategoryId)
          : undefined,
        categories: data.categories.map((c) =>
          toConvexTableId("categories", c)
        ),
      });
      form.reset();
      setModalType(null);
      onFinished?.();
      return returnValue.id;
    } catch (error) {
      if (
        error instanceof Error &&
        error.message.startsWith("Item name already exists")
      ) {
        form.setError("name", {
          message: error.message,
          type: "value",
        });
      }
      return false;
    }
  };

  const handleSubmitAndView = async (data: ItemFormData) => {
    const result = await handleFormSubmit(data);
    if (result) {
      void navigate({ to: `/menus/${menuId}/items/${result}` });
    }
  };

  const handleCreateSalesCategory = (name: string) => {
    void navigate({
      to: `/menus/${menuId}/items/new/create-sales-category`,
      search: { name },
    });
  };

  const handleCreateTaxCategory = (name: string) => {
    void navigate({
      to: `/menus/${menuId}/items/new/create-tax-category`,
      search: { name },
    });
  };

  return (
    <DialogContent>
      <DialogHeader className="items-center space-y-0">
        <DialogTitle>Create Item</DialogTitle>
        <DialogDescription className="body">
          This item will be added to <b>{menuName ?? "this menu"}</b>
        </DialogDescription>
      </DialogHeader>
      <Form {...form}>
        <form
          className="space-y-5"
          onSubmit={form.handleSubmit(handleFormSubmit)}
        >
          <div className="flex gap-5">
            <div className="w-full space-y-5">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Item Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Price</FormLabel>
                    <FormControl>
                      <PriceInput
                        field={{ ...field, value: field.value ?? "" }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="imageUrl"
              render={({ field }) => (
                <ImageDropzone
                  onValueChange={field.onChange}
                  value={field.value ?? null}
                />
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description / Ingredients</FormLabel>
                <FormControl>
                  <textarea
                    className="border-border focus-visible:border-foreground field-sizing-content min-h-20 w-full rounded-2xl border px-5 py-3 outline-0"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <CategoryFields
            categories={categories}
            form={form}
            onCreateSalesCategory={handleCreateSalesCategory}
            onCreateTaxCategory={handleCreateTaxCategory}
            salesCategories={salesCategories}
            taxCategories={taxCategories ?? []}
          />
          <div className="float-start w-1/2">
            <Button
              onClick={() => {
                form.reset();
                handleClose();
              }}
              size="default"
              type="reset"
              variant="ghost"
            >
              Cancel
            </Button>
          </div>
          <div className="float-end flex w-1/2 space-x-2">
            <Button
              className="w-2/3"
              disabled={createItemMutation.isPending}
              onClick={form.handleSubmit(handleSubmitAndView)}
              size="default"
              type="button"
              variant="secondary"
            >
              Save and view item
            </Button>
            <Button
              className="w-1/3"
              disabled={createItemMutation.isPending}
              size="default"
              type="submit"
            >
              Save
            </Button>
          </div>
        </form>
      </Form>
    </DialogContent>
  );
};
